import { eventDurationHours } from "./selectors";
import { durationHours } from "./settings";
import type { OutlookCandidate } from "./types";

function addDays(dateText: string, days: number): string {
  const date = new Date(`${dateText}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

export function candidateDates(event: Pick<OutlookCandidate, "start" | "end" | "allDay">): string[] {
  const startDate = event.start.slice(0, 10);
  let endDate = event.end.slice(0, 10);
  if (!event.allDay && endDate > startDate && event.end.slice(11, 16) === "00:00") {
    endDate = addDays(endDate, -1);
  }

  const dates: string[] = [];
  for (let date = startDate; date <= endDate; date = addDays(date, 1)) dates.push(date);
  return dates;
}

/** 候補一覧の絞り込み条件。値が空・0・false のものは条件として使わない。 */
export interface CandidateFilter {
  /** 空文字なら全チーム。 */
  teamAddress: string;
  /** この時間未満の予定を隠す。終日予定は時間で判定しない。0 なら隠さない。 */
  minDurationHours: number;
  /** 繰り返し予定（定例）を隠す。 */
  excludeRecurring: boolean;
}

export const DEFAULT_CANDIDATE_FILTER: CandidateFilter = {
  teamAddress: "",
  minDurationHours: 0,
  excludeRecurring: false,
};

/** 既定値と異なる条件が1つでもあれば true。解除ボタンや「条件あり」の表示に使う。 */
export function isFilterActive(filter: CandidateFilter): boolean {
  return (
    filter.teamAddress !== DEFAULT_CANDIDATE_FILTER.teamAddress ||
    filter.minDurationHours !== DEFAULT_CANDIDATE_FILTER.minDurationHours ||
    filter.excludeRecurring !== DEFAULT_CANDIDATE_FILTER.excludeRecurring
  );
}

/**
 * 最小時間の入力欄（文字列）を絞り込みに使う時間へ変換する。
 * 入力途中の文字列を壊さないよう、欄は文字列のまま持ち、使うときだけここで数値にする。
 * 空欄・数値でない値は 0（絞り込まない）、範囲外は 0〜24 に丸める。
 */
export function parseMinDurationHours(text: string): number {
  const value = Number(text);
  if (text.trim() === "" || !Number.isFinite(value)) return 0;
  return durationHours(Math.min(Math.max(value, 0), 24)) ?? 0;
}

export type CandidateHiddenReason = "team" | "duration" | "recurring";

/** 候補が絞り込みで隠れる理由。表示されるなら undefined。 */
export function candidateHiddenReason(
  event: OutlookCandidate,
  filter: CandidateFilter,
): CandidateHiddenReason | undefined {
  if (filter.teamAddress && !event.teamAddresses.includes(filter.teamAddress)) return "team";
  if (filter.excludeRecurring && event.recurring === true) return "recurring";
  // 終日予定は「短い予定」ではないので最小時間では隠さない。日時を読めない予定は
  // 長さ不明として隠す（タイムラインの最小表示時間と同じ規則・同じ関数で測る）。
  if (filter.minDurationHours > 0 && !event.allDay) {
    const hours = eventDurationHours(event);
    if (hours === undefined || hours < filter.minDurationHours) return "duration";
  }
  return undefined;
}

export function filterCandidates(events: OutlookCandidate[], filter: CandidateFilter): OutlookCandidate[] {
  return events.filter((event) => candidateHiddenReason(event, filter) === undefined);
}
